// Onboarding hero for the marketplace. Bundles headline + subtitle
// + the prominent search pill directly underneath. The search bar
// is the "vedeta paginii" — the visitor's eye should land on it
// the moment they arrive, full-width on mobile for thumb tapping.
//
// "blănosul" uses the serif-italic Accent (brand orange) as the
// warm-editorial highlight word.

import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import PillSearchBar from './PillSearchBar';
import { Accent } from '../ui/PageHero';

// Two states:
//   • Full (list mode / mobile / landing) — big editorial headline (2 rows) +
//     subtitle + a prominent full-width search bar.
//   • collapsed (desktop map view) — the headline + subtitle animate OUT (fade +
//     slide up + height collapse) and the bar shrinks into a single slim toolbar
//     row: a compact search + the `children` (Filtre / Listă-Hartă / Sortare).
//
// The collapse is a deliberate transition, not a jerky reflow: the text exits
// with opacity→0 / translateY(-20px) / height→0 (Framer AnimatePresence) while
// the search row glides up via a layout animation, so the map below slides up to
// fill the freed space. Honors prefers-reduced-motion (plain swap, no motion).
export default function ExploreHero({ onOpenWhere, collapsed = false, children }) {
  const reduced = useReducedMotion();
  const anim = !reduced;
  return (
    <motion.section
      layout={anim}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`mx-auto w-full px-4 sm:px-6 lg:px-8 ${collapsed ? 'max-w-7xl pb-3 pt-3' : 'max-w-4xl pb-4 pt-6 lg:pt-10'}`}
    >
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="hero-text"
            initial={anim ? { opacity: 0, y: -12 } : false}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={anim ? { opacity: 0, y: -20, height: 0, marginBottom: 0 } : { opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="overflow-hidden text-center"
          >
            <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Răsfață-ți <Accent>blănosul</Accent> la cele mai bune saloane din oraș
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-ink-500 sm:mt-4 sm:text-base lg:text-lg">
              Programează-te online la cele mai bune saloane de toaletaj animale din orașul tău. Tuns, spălat, trimming, tăiat unghii — rapid, simplu și cu cele mai bune prețuri.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search row. Collapsed = a slim toolbar (compact search + the controls
          passed as children); otherwise a full-width prominent bar. */}
      <motion.div
        layout={anim}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={collapsed ? 'flex flex-wrap items-center justify-between gap-4' : 'mt-5 sm:mt-6'}
      >
        <div className={collapsed ? 'min-w-0 flex-1 lg:max-w-xl' : 'w-full'}>
          <PillSearchBar onOpenWhere={onOpenWhere} compact={collapsed} />
        </div>
        {/* The search is one block; the tools (Filtre / Listă-Hartă / Sortare) are
            another — a tighter inner gap, a wider gap to the search, so the two
            groups breathe. */}
        {collapsed && children && (
          <div className="flex shrink-0 items-center gap-3">{children}</div>
        )}
      </motion.div>
    </motion.section>
  );
}
